import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  CheckCircle2, 
  XCircle,
  Shield,
  Clock,
  Hash,
  Loader2,
  ExternalLink,
  Link2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";

import { useAppConfig } from "../lib/AppConfigContext";

export default function VerifyVotePage() {
  const { appName } = useAppConfig();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [consultaId, setConsultaId] = useState(searchParams.get('consulta') || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!consultaId.trim() || !verificationCode.trim()) {
      toast.error(t('voting.enterIdAndCode'));
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post("/audit/verify-vote", {
        consulta_id: consultaId,
        verification_code: verificationCode
      });
      setResult(response.data);
      
      if (response.data.found) {
        toast.success(t('voting.voteVerifiedSuccess'));
      } else {
        toast.error(t('voting.voteNotFound'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.verifyVote'));
      setResult({ found: false, vote_registered: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verify-vote-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-slate-900">{appName}</h1>
            <p className="text-xs text-slate-500">{t('voting.voteVerification')}</p>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl font-heading font-bold text-slate-900 mb-2">
            Verifica el teu vot
          </h2>
          <p className="text-slate-600">
            Comprova que el teu vot ha estat registrat correctament
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm"
        >
          <form onSubmit={handleVerify}>
            <div className="space-y-4">
              <div className="form-group">
                <Label htmlFor="consultaId" className="form-label">
                  ID de la Consulta
                </Label>
                <Input
                  id="consultaId"
                  value={consultaId}
                  onChange={(e) => setConsultaId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="h-12 font-mono text-sm"
                  data-testid="consulta-id-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  L'ID de la consulta apareix a l'URL de votació
                </p>
              </div>

              <div className="form-group">
                <Label htmlFor="verificationCode" className="form-label">
                  ID Públic o Hash de Verificació
                </Label>
                <Input
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="ID públic o hash de verificació"
                  className="h-12 font-mono text-sm"
                  data-testid="verification-code-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  L'ID públic o el hash que vas rebre al votar
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                disabled={loading}
                data-testid="verify-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Verificar vot
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 p-6 rounded-lg border ${
                result.found 
                  ? "bg-emerald-50 border-emerald-200" 
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  result.found ? "bg-emerald-100" : "bg-red-100"
                }`}>
                  {result.found ? (
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold text-lg ${
                    result.found ? "text-emerald-900" : "text-red-900"
                  }`}>
                    {result.found ? "Vot verificat!" : "Vot no trobat"}
                  </h3>
                  <p className={`text-sm ${
                    result.found ? "text-emerald-700" : "text-red-700"
                  }`}>
                    {result.found 
                      ? "El teu vot ha estat registrat correctament al sistema."
                      : "No s'ha trobat cap vot amb aquest codi de verificació."}
                  </p>
                  
                  {result.found && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-emerald-800">
                        <Hash className="h-4 w-4" />
                        <span className="font-mono">{result.id_publica}</span>
                      </div>
                      {result.timestamp && (
                        <div className="flex items-center gap-2 text-sm text-emerald-800">
                          <Clock className="h-4 w-4" />
                          <span>{result.timestamp}</span>
                        </div>
                      )}
                      {result.solana_tx && (
                        <div className="mt-3 p-3 bg-emerald-100 border border-emerald-300 rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 mb-1">
                            <Link2 className="h-4 w-4" />
                            {t('blockchain.solanaVerification')}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-emerald-700">
                            <span className="font-mono truncate flex-1" data-testid="solana-tx-signature">
                              {result.solana_tx}
                            </span>
                            <a
                              href={`https://solscan.io/tx/${result.solana_tx}${result.blockchain_network === 'devnet' ? '?cluster=devnet' : ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 whitespace-nowrap"
                              data-testid="solscan-link"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {t('blockchain.viewOnSolscan')}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 text-center text-sm text-slate-500"
        >
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-4 w-4" />
            <span>{t('voting.verificationNoReveal')}</span>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
